import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	AlertDescription,
	AlertIcon,
	Badge,
	Box,
	Button,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	HStack,
	IconButton,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	SimpleGrid,
	Stack,
	Text,
	Textarea,
	Tooltip,
	useClipboard,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import {
	FaCheck,
	FaCopy,
	FaEdit,
	FaEnvelope,
	FaPlus,
	FaTrash,
	FaUserCheck,
} from 'react-icons/fa';
import {
	useDeleteLeadContact,
	useDeleteLeadEvent,
	useLogLeadEvent,
	useSaveLeadContact,
	useUpdateLead,
} from '../../../hooks/CRM/useOutreach';
import VerifyEmailsModal from './VerifyEmailsModal';
import { SendViaGmailButton } from './GmailSend';
import { useOutreachEmailSettings } from '../../../hooks/useOutreachEmailSettings';
import {
	CONTACT_CONFIDENCE,
	CONTACT_TARGETS,
	EVENT_LABELS,
	confidenceColor,
	emailVerificationOf,
	fmtDate,
	fmtDateTime,
	fromInputDate,
	isDropped,
	mailtoLink,
	primaryOf,
	qualityColor,
	qualityOf,
	revenueDescription,
	revenueLabel,
	tagLabel,
	targetColor,
	timeAgo,
	toInputDate,
	userName,
} from './leadMeta';

export const CopyButton = ({ value, label, size = 'sm' }) => {
	const { onCopy, hasCopied } = useClipboard(value || '');
	return (
		<Button
			size={size}
			variant='outline'
			leftIcon={hasCopied ? <FaCheck /> : <FaCopy />}
			onClick={onCopy}
			isDisabled={!value}>
			{hasCopied ? 'Copied' : label}
		</Button>
	);
};

const CopyIcon = ({ value, label }) => {
	const { onCopy, hasCopied } = useClipboard(value || '');
	return (
		<IconButton
			size='xs'
			variant='ghost'
			aria-label={label}
			icon={hasCopied ? <FaCheck /> : <FaCopy />}
			onClick={onCopy}
		/>
	);
};

const useErrorToast = () => {
	const toast = useToast();
	return (err) =>
		toast({ status: 'error', title: err.message, isClosable: true });
};

export const LogModal = ({ lead, companyId, type, onClose }) => {
	const showError = useErrorToast();
	const logEvent = useLogLeadEvent(companyId);
	const contacts = (lead?.contacts || []).filter((c) => !isDropped(c));
	const [contactName, setContactName] = useState('');
	const [note, setNote] = useState('');
	const [date, setDate] = useState(toInputDate(new Date()));

	useEffect(() => {
		if (!type) return;
		setContactName(primaryOf(lead)?.name ?? '');
		setNote('');
		setDate(toInputDate(new Date()));
	}, [type, lead]);

	const followUpText =
		type === 'follow_up_1'
			? lead?.draft?.followUp1
			: type === 'follow_up_2'
				? lead?.draft?.followUp2
				: '';

	const submit = async (e) => {
		e.preventDefault();
		const today = toInputDate(new Date());
		const at =
			date === today
				? new Date().toISOString()
				: (fromInputDate(date) ?? undefined);
		try {
			await logEvent.mutateAsync({
				id: lead._id,
				data: { type, note: note.trim(), contactName, at },
			});
			onClose();
		} catch (err) {
			showError(err);
		}
	};

	return (
		<Modal isOpen={!!type} onClose={onClose}>
			<ModalOverlay />
			<ModalContent as='form' onSubmit={submit}>
				<ModalHeader>{type ? EVENT_LABELS[type] : ''}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack spacing={4}>
						<Text fontSize='sm' color='gray.600'>
							{lead?.name}
						</Text>
						{followUpText && (
							<Box>
								<HStack justify='space-between' mb={1}>
									<Text fontSize='xs' fontWeight='semibold' color='gray.600'>
										Follow-up text
									</Text>
									<CopyButton value={followUpText} label='Copy' size='xs' />
								</HStack>
								<Textarea
									value={followUpText}
									isReadOnly
									rows={6}
									fontSize='sm'
									aria-label='Follow-up text'
								/>
							</Box>
						)}
						{contacts.length > 0 && (
							<FormControl>
								<FormLabel>Contact</FormLabel>
								<Select
									value={contactName}
									onChange={(e) => setContactName(e.target.value)}>
									<option value=''>No specific contact</option>
									{contacts.map((c) => (
										<option key={c._id} value={c.name}>
											{c.name || c.email}
											{c.role ? ` (${c.role.slice(0, 50)})` : ''}
										</option>
									))}
								</Select>
							</FormControl>
						)}
						<FormControl>
							<FormLabel>Date</FormLabel>
							<Input
								type='date'
								value={date}
								onChange={(e) => setDate(e.target.value)}
								max={toInputDate(new Date())}
							/>
						</FormControl>
						<FormControl>
							<FormLabel>Note (optional)</FormLabel>
							<Textarea
								value={note}
								onChange={(e) => setNote(e.target.value)}
								rows={3}
								placeholder='e.g. Sent to the alternative address after a bounce'
							/>
						</FormControl>
					</Stack>
				</ModalBody>
				<ModalFooter gap={2}>
					<Button variant='ghost' onClick={onClose}>
						Cancel
					</Button>
					<Button
						type='submit'
						colorScheme='teal'
						isLoading={logEvent.isPending}
						leftIcon={<FaCheck />}>
						Save
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export const EmailTab = ({ lead, companyId, canUpdate }) => {
	const toast = useToast();
	const showError = useErrorToast();
	const updateLead = useUpdateLead(companyId);
	const { withSignature } = useOutreachEmailSettings(companyId);
	const [draft, setDraft] = useState(lead.draft);

	useEffect(() => setDraft(lead.draft), [lead._id, lead.draft]);

	const dirty = Object.keys(draft).some((k) => draft[k] !== lead.draft[k]);
	const set = (field) => (e) =>
		setDraft((d) => ({ ...d, [field]: e.target.value }));
	const primary = primaryOf(lead);
	const to = draft.to || primary?.email || '';
	const canOpen = !!(to && draft.body);

	const save = async () => {
		try {
			await updateLead.mutateAsync({ id: lead._id, data: { draft } });
			toast({ status: 'success', title: 'Email saved' });
		} catch (err) {
			showError(err);
		}
	};

	return (
		<Stack spacing={4}>
			{!lead.draft.body && !draft.body && (
				<Alert status='info' borderRadius='md' fontSize='sm'>
					<AlertIcon />
					<AlertDescription>
						No email yet. Write one here, or import an email-drafts CSV.
					</AlertDescription>
				</Alert>
			)}
			<FormControl>
				<FormLabel>To</FormLabel>
				<Input
					value={draft.to}
					onChange={set('to')}
					placeholder={primary?.email || 'name@company.co.uk'}
					isReadOnly={!canUpdate}
				/>
				{!draft.to && primary?.email && (
					<FormHelperText>
						Empty means the primary contact: {primary.email}
					</FormHelperText>
				)}
			</FormControl>
			<FormControl>
				<FormLabel>Subject</FormLabel>
				<Input
					value={draft.subject}
					onChange={set('subject')}
					isReadOnly={!canUpdate}
				/>
			</FormControl>
			<FormControl>
				<FormLabel>Email</FormLabel>
				<Textarea
					value={draft.body}
					onChange={set('body')}
					rows={14}
					fontSize='sm'
					isReadOnly={!canUpdate}
				/>
			</FormControl>
			<Flex gap={2} wrap='wrap'>
				<CopyButton value={draft.subject} label='Copy subject' />
				<CopyButton value={draft.body} label='Copy email' />
				<Button
					as='a'
					size='sm'
					variant='outline'
					leftIcon={<FaEnvelope />}
					href={mailtoLink(to, draft.subject, withSignature(draft.body))}
					pointerEvents={canOpen ? undefined : 'none'}
					opacity={canOpen ? 1 : 0.5}>
					Open in mail app
				</Button>
				{canUpdate && (
					<SendViaGmailButton
						lead={lead}
						companyId={companyId}
						draft={draft}
						beforeSend={
							dirty
								? () => updateLead.mutateAsync({ id: lead._id, data: { draft } })
								: undefined
						}
					/>
				)}
				{canUpdate && (
					<Button
						size='sm'
						colorScheme='teal'
						variant='outline'
						onClick={save}
						isLoading={updateLead.isPending}
						isDisabled={!dirty}
						ml='auto'>
						Save changes
					</Button>
				)}
			</Flex>
			<FormControl>
				<FormLabel>Follow-up 1</FormLabel>
				<Textarea
					value={draft.followUp1}
					onChange={set('followUp1')}
					rows={6}
					fontSize='sm'
					isReadOnly={!canUpdate}
				/>
			</FormControl>
			<FormControl>
				<FormLabel>Follow-up 2</FormLabel>
				<Textarea
					value={draft.followUp2}
					onChange={set('followUp2')}
					rows={6}
					fontSize='sm'
					isReadOnly={!canUpdate}
				/>
			</FormControl>
		</Stack>
	);
};

const EMPTY_CONTACT = {
	name: '',
	role: '',
	email: '',
	phone: '',
	alternative: '',
	confidence: '',
	target: '',
	notes: '',
};

const ContactModal = ({ isOpen, initial, onClose, onSubmit, isSaving }) => {
	const [form, setForm] = useState(EMPTY_CONTACT);
	const [error, setError] = useState('');

	useEffect(() => {
		if (!isOpen) return;
		setForm(initial ? { ...EMPTY_CONTACT, ...initial } : EMPTY_CONTACT);
		setError('');
	}, [isOpen, initial]);

	const set = (field) => (e) =>
		setForm((f) => ({ ...f, [field]: e.target.value }));

	const submit = async (e) => {
		e.preventDefault();
		setError('');
		try {
			const data = Object.fromEntries(
				Object.keys(EMPTY_CONTACT).map((k) => [k, form[k] ?? '']),
			);
			await onSubmit(data);
			onClose();
		} catch (err) {
			setError(err.message);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size='lg'>
			<ModalOverlay />
			<ModalContent as='form' onSubmit={submit}>
				<ModalHeader>{initial ? 'Edit contact' : 'Add contact'}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack spacing={3}>
						{error && (
							<Alert status='error' borderRadius='md' fontSize='sm'>
								<AlertIcon />
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
							<FormControl>
								<FormLabel>Name</FormLabel>
								<Input value={form.name} onChange={set('name')} autoFocus />
							</FormControl>
							<FormControl>
								<FormLabel>Role</FormLabel>
								<Input value={form.role} onChange={set('role')} />
							</FormControl>
							<FormControl>
								<FormLabel>Email</FormLabel>
								<Input type='email' value={form.email} onChange={set('email')} />
							</FormControl>
							<FormControl>
								<FormLabel>Phone</FormLabel>
								<Input value={form.phone} onChange={set('phone')} />
							</FormControl>
							<FormControl>
								<FormLabel>Alternative email</FormLabel>
								<Input value={form.alternative} onChange={set('alternative')} />
							</FormControl>
							<FormControl>
								<FormLabel>Target</FormLabel>
								<Select value={form.target} onChange={set('target')}>
									<option value=''>–</option>
									{CONTACT_TARGETS.map((t) => (
										<option key={t} value={t}>
											{t}
										</option>
									))}
								</Select>
							</FormControl>
							<FormControl>
								<FormLabel>Confidence</FormLabel>
								<Select value={form.confidence} onChange={set('confidence')}>
									<option value=''>–</option>
									{CONTACT_CONFIDENCE.map((t) => (
										<option key={t} value={t}>
											{t}
										</option>
									))}
								</Select>
							</FormControl>
						</SimpleGrid>
						<FormControl>
							<FormLabel>Notes</FormLabel>
							<Textarea value={form.notes} onChange={set('notes')} rows={3} />
						</FormControl>
					</Stack>
				</ModalBody>
				<ModalFooter gap={2}>
					<Button variant='ghost' onClick={onClose}>
						Cancel
					</Button>
					<Button type='submit' colorScheme='teal' isLoading={isSaving}>
						Save contact
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export const ContactsTab = ({ lead, companyId, canUpdate }) => {
	const showError = useErrorToast();
	const modal = useDisclosure();
	const saveContact = useSaveLeadContact(companyId);
	const deleteContact = useDeleteLeadContact(companyId);
	const [editing, setEditing] = useState(null);
	/** undefined = closed, null = all contacts, array = those contact ids. */
	const [verifyIds, setVerifyIds] = useState(undefined);
	const withEmail = lead.contacts.filter((c) => c.email);
	const unchecked = withEmail.filter((c) => !emailVerificationOf(c)).length;
	const sorted = useMemo(
		() =>
			[...lead.contacts].sort(
				(a, b) => Number(isDropped(a)) - Number(isDropped(b)),
			),
		[lead.contacts],
	);

	const openAdd = () => {
		setEditing(null);
		modal.onOpen();
	};
	const openEdit = (c) => {
		setEditing(c);
		modal.onOpen();
	};
	const remove = async (c) => {
		if (!window.confirm(`Remove ${c.name || c.email} from ${lead.name}?`)) return;
		try {
			await deleteContact.mutateAsync({ id: lead._id, contactId: c._id });
		} catch (err) {
			showError(err);
		}
	};
	return (
		<Stack spacing={3}>
			{canUpdate && (
				<Flex justify='flex-end' gap={2} wrap='wrap'>
					{withEmail.length > 0 && (
						<Button
							size='sm'
							variant='outline'
							colorScheme='teal'
							leftIcon={<FaUserCheck />}
							onClick={() => setVerifyIds(null)}>
							Verify all emails{unchecked ? ` (${unchecked} unchecked)` : ''}
						</Button>
					)}
					<Button
						size='sm'
						colorScheme='teal'
						leftIcon={<FaPlus />}
						onClick={openAdd}>
						Add contact
					</Button>
				</Flex>
			)}
			{!sorted.length && (
				<Text fontSize='sm' color='gray.500'>
					No contacts yet.
				</Text>
			)}
			{sorted.map((c) => {
				const dropped = isDropped(c);
				const check = emailVerificationOf(c);
				return (
					<Box
						key={c._id}
						borderWidth='1px'
						borderColor='gray.200'
						borderRadius='lg'
						p={3}
						opacity={dropped ? 0.6 : 1}>
						<Flex justify='space-between' gap={3} align='flex-start'>
							<Box minW={0}>
								<HStack spacing={2} wrap='wrap'>
									<Text
										fontWeight='bold'
										textDecoration={dropped ? 'line-through' : undefined}>
										{c.name || c.email}
									</Text>
									{c.target && (
										<Badge colorScheme={targetColor(c.target)}>{c.target}</Badge>
									)}
									{c.confidence && (
										<Badge colorScheme={confidenceColor(c.confidence)}>
											{c.confidence}
										</Badge>
									)}
									{c._id === lead.primaryContactId && (
										<Badge colorScheme='teal'>Email goes here</Badge>
									)}
								</HStack>
								{c.role && (
									<Text fontSize='sm' color='gray.600'>
										{c.role}
									</Text>
								)}
							</Box>
							{canUpdate && (
								<HStack spacing={0}>
									<IconButton
										size='xs'
										variant='ghost'
										aria-label='Edit contact'
										icon={<FaEdit />}
										onClick={() => openEdit(c)}
									/>
									<IconButton
										size='xs'
										variant='ghost'
										aria-label='Remove contact'
										icon={<FaTrash />}
										onClick={() => remove(c)}
									/>
								</HStack>
							)}
						</Flex>
						{c.email && (
							<HStack spacing={1} mt={1} wrap='wrap'>
								<Text fontSize='sm' fontFamily='mono'>
									{c.email}
								</Text>
								<CopyIcon value={c.email} label={`Copy ${c.email}`} />
								{check && (
									<Tooltip
										hasArrow
										label={[check.subStatus, `Checked ${timeAgo(check.checkedAt)}`]
											.filter(Boolean)
											.join(' · ')}>
										<Badge colorScheme={check.color} ml={1}>
											{check.label}
										</Badge>
									</Tooltip>
								)}
								{canUpdate && (
									<Button
										size='xs'
										variant='ghost'
										colorScheme='teal'
										ml={1}
										onClick={() => setVerifyIds([c._id])}>
										{check ? 'Re-check' : 'Verify'}
									</Button>
								)}
							</HStack>
						)}
						{c.phone && (
							<Text fontSize='sm' color='gray.600'>
								{c.phone}
							</Text>
						)}
						{c.alternative && (
							<Text fontSize='xs' color='gray.500'>
								Alternative: {c.alternative}
							</Text>
						)}
						{c.notes && (
							<Text fontSize='sm' mt={1}>
								{c.notes}
							</Text>
						)}
						{(c.source || c.evidence) && (
							<Text
								fontSize='xs'
								color='gray.500'
								mt={1}
								noOfLines={3}
								title={[c.source, c.evidence].filter(Boolean).join(' · ')}>
								{[c.source, c.evidence].filter(Boolean).join(' · ')}
							</Text>
						)}
					</Box>
				);
			})}
			<ContactModal
				isOpen={modal.isOpen}
				initial={editing}
				onClose={modal.onClose}
				isSaving={saveContact.isPending}
				onSubmit={(data) =>
					saveContact.mutateAsync({ id: lead._id, contactId: editing?._id, data })
				}
			/>
			{verifyIds !== undefined && (
				<VerifyEmailsModal
					isOpen
					onClose={() => setVerifyIds(undefined)}
					lead={lead}
					companyId={companyId}
					contactIds={verifyIds}
				/>
			)}
		</Stack>
	);
};

export const TimelineTab = ({ lead, companyId, canUpdate }) => {
	const showError = useErrorToast();
	const logEvent = useLogLeadEvent(companyId);
	const deleteEvent = useDeleteLeadEvent(companyId);
	const [note, setNote] = useState('');

	const addNote = async (e) => {
		e.preventDefault();
		if (!note.trim()) return;
		try {
			await logEvent.mutateAsync({
				id: lead._id,
				data: { type: 'note', note: note.trim() },
			});
			setNote('');
		} catch (err) {
			showError(err);
		}
	};

	const remove = async (eventId) => {
		if (!window.confirm('Delete this timeline entry? The status stays as it is.'))
			return;
		try {
			await deleteEvent.mutateAsync({ id: lead._id, eventId });
		} catch (err) {
			showError(err);
		}
	};

	return (
		<Stack spacing={4}>
			{canUpdate && (
				<Box as='form' onSubmit={addNote}>
					<Textarea
						value={note}
						onChange={(e) => setNote(e.target.value)}
						rows={2}
						placeholder='Add a note, e.g. what they said on the phone'
					/>
					<Flex justify='flex-end' mt={2}>
						<Button
							size='sm'
							colorScheme='teal'
							type='submit'
							isLoading={logEvent.isPending}
							isDisabled={!note.trim()}>
							Add note
						</Button>
					</Flex>
				</Box>
			)}
			{lead.events.map((ev) => (
				<Flex
					key={ev._id}
					gap={3}
					borderBottomWidth='1px'
					borderColor='gray.100'
					pb={3}>
					<Box flex={1} minW={0}>
						<HStack spacing={2} wrap='wrap'>
							<Text fontSize='sm' fontWeight='semibold'>
								{EVENT_LABELS[ev.type] ?? ev.type}
							</Text>
							{ev.contactName && (
								<Text fontSize='sm' color='gray.600'>
									· {ev.contactName}
								</Text>
							)}
						</HStack>
						<Text fontSize='xs' color='gray.500' title={fmtDateTime(ev.at)}>
							{fmtDate(ev.at)} · {timeAgo(ev.at)}
							{ev.performedBy ? ` · ${userName(ev.performedBy)}` : ''}
						</Text>
						{ev.note && (
							<Text fontSize='sm' mt={1} whiteSpace='pre-wrap'>
								{ev.note}
							</Text>
						)}
					</Box>
					{canUpdate && (
						<IconButton
							size='xs'
							variant='ghost'
							aria-label='Delete entry'
							icon={<FaTrash />}
							onClick={() => remove(ev._id)}
						/>
					)}
				</Flex>
			))}
		</Stack>
	);
};

const QualitySummary = ({ lead }) => {
	const quality = qualityOf(lead);
	if (!quality && !lead.industry && !(lead.tags || []).length) return null;
	return (
		<Box
			borderWidth='1px'
			borderColor='gray.200'
			borderRadius='lg'
			p={4}
			bg='gray.50'>
			<HStack spacing={3} mb={quality?.reasons.length ? 3 : 0} wrap='wrap'>
				{quality ? (
					<>
						<Badge colorScheme={qualityColor(quality.grade)} fontSize='md' px={2}>
							{quality.grade}
						</Badge>
						<Text fontWeight='semibold' color='gray.800'>
							{quality.score !== null
								? `Lead quality ${quality.score}/100`
								: 'Lead quality'}
						</Text>
					</>
				) : (
					<Text fontWeight='semibold' color='gray.800'>
						Not scored yet
					</Text>
				)}
				{lead.industry && (
					<Badge colorScheme='purple' variant='subtle' textTransform='none'>
						{lead.industry}
					</Badge>
				)}
				{(lead.tags || []).map((t) => (
					<Badge key={t} variant='outline' colorScheme='gray' textTransform='none'>
						{tagLabel(t)}
					</Badge>
				))}
			</HStack>
			{quality?.reasons.length ? (
				<Stack spacing={1}>
					{quality.reasons.map((r) => (
						<Text key={r} fontSize='sm' color='gray.700'>
							• {r}
						</Text>
					))}
				</Stack>
			) : null}
			{lead.companyNumber && (
				<Text fontSize='sm' color='gray.600' mt={3}>
					{'Companies House: '}
					<Box
						as='a'
						href={`https://find-and-update.company-information.service.gov.uk/company/${encodeURIComponent(lead.companyNumber)}`}
						target='_blank'
						rel='noreferrer'
						color='teal.600'
						fontWeight='semibold'>
						{lead.companyNumber}
					</Box>
				</Text>
			)}
		</Box>
	);
};

export const DetailsTab = ({
	lead,
	companyId,
	canUpdate,
	canDelete,
	onDelete,
	isDeleting,
}) => {
	const toast = useToast();
	const showError = useErrorToast();
	const updateLead = useUpdateLead(companyId);
	const [form, setForm] = useState({
		name: '',
		website: '',
		priority: '',
		industry: '',
		companyNumber: '',
		tags: '',
		pitch: '',
		notes: '',
		nextAction: '',
		nextActionAt: '',
	});

	useEffect(() => {
		setForm({
			name: lead.name,
			website: lead.website || '',
			priority: lead.priority || '',
			industry: lead.industry || '',
			companyNumber: lead.companyNumber || '',
			tags: (lead.tags || []).join(', '),
			pitch: lead.pitch || '',
			notes: lead.notes || '',
			nextAction: lead.nextAction || '',
			nextActionAt: toInputDate(lead.nextActionAt),
		});
	}, [lead]);

	const set = (field) => (e) =>
		setForm((f) => ({ ...f, [field]: e.target.value }));
	const revenue = revenueLabel(lead);

	const save = async (e) => {
		e.preventDefault();
		try {
			await updateLead.mutateAsync({
				id: lead._id,
				data: {
					...form,
					tags: form.tags
						.split(/[,;|]/)
						.map((t) => t.trim())
						.filter(Boolean),
					nextActionAt: fromInputDate(form.nextActionAt),
				},
			});
			toast({ status: 'success', title: 'Saved' });
		} catch (err) {
			showError(err);
		}
	};

	return (
		<Stack as='form' spacing={4} onSubmit={save}>
			<QualitySummary lead={lead} />
			<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
				<FormControl>
					<FormLabel>Company name</FormLabel>
					<Input value={form.name} onChange={set('name')} isReadOnly={!canUpdate} />
				</FormControl>
				<FormControl>
					<FormLabel>Website</FormLabel>
					<Input
						value={form.website}
						onChange={set('website')}
						placeholder='https://'
						isReadOnly={!canUpdate}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Tier</FormLabel>
					<Input
						value={lead.tier || ''}
						placeholder='Set by CSV import'
						isReadOnly
						bg='gray.50'
						title={lead.tier}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Revenue</FormLabel>
					<Input
						value={revenueDescription(revenue)}
						placeholder='Set by CSV import'
						isReadOnly
						bg='gray.50'
						title={revenue.hint}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Priority</FormLabel>
					<Input
						value={form.priority}
						onChange={set('priority')}
						placeholder='e.g. A, B, C or High'
						isReadOnly={!canUpdate}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Industry</FormLabel>
					<Input
						value={form.industry}
						onChange={set('industry')}
						placeholder='e.g. Construction'
						isReadOnly={!canUpdate}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Tags</FormLabel>
					<Input
						value={form.tags}
						onChange={set('tags')}
						placeholder='Comma separated, e.g. construction, trades-roofing'
						isReadOnly={!canUpdate}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Companies House number</FormLabel>
					<Input
						value={form.companyNumber}
						onChange={set('companyNumber')}
						placeholder='e.g. 04009614'
						isReadOnly={!canUpdate}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Next step</FormLabel>
					<Input
						value={form.nextAction}
						onChange={set('nextAction')}
						placeholder='e.g. Call to confirm address'
						isReadOnly={!canUpdate}
					/>
				</FormControl>
				<FormControl>
					<FormLabel>Due</FormLabel>
					<Input
						type='date'
						value={form.nextActionAt}
						onChange={set('nextActionAt')}
						isReadOnly={!canUpdate}
					/>
				</FormControl>
			</SimpleGrid>
			<FormControl>
				<FormLabel>Pitch angle</FormLabel>
				<Textarea
					value={form.pitch}
					onChange={set('pitch')}
					rows={8}
					isReadOnly={!canUpdate}
				/>
			</FormControl>
			<FormControl>
				<FormLabel>Notes</FormLabel>
				<Textarea
					value={form.notes}
					onChange={set('notes')}
					rows={4}
					isReadOnly={!canUpdate}
				/>
			</FormControl>
			<Flex justify='space-between'>
				{canDelete ? (
					<Button
						variant='ghost'
						colorScheme='red'
						leftIcon={<FaTrash />}
						onClick={onDelete}
						isLoading={isDeleting}>
						Delete lead
					</Button>
				) : (
					<span />
				)}
				{canUpdate && (
					<Button type='submit' colorScheme='teal' isLoading={updateLead.isPending}>
						Save
					</Button>
				)}
			</Flex>
		</Stack>
	);
};
